import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAvailableSlots, useBookSlot, useBookingStatus } from '../api/booking';
import SlotPicker from '../components/SlotPicker/SlotPicker';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import type { Slot } from '../types/booking';

export default function BookingPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const { data, isLoading, error } = useAvailableSlots(token || '');
  const { data: statusData } = useBookingStatus(token || '');
  const bookSlot = useBookSlot(token || '');

  // If already has a booking, redirect to status page
  if (statusData?.hasBooking) {
    navigate(`/status/${token}`, { replace: true });
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-16">
        <ErrorMessage
          title="Ungültiger Link"
          message="Der Buchungslink ist ungültig oder abgelaufen. Bitte kontaktieren Sie uns für einen neuen Link."
        />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const handleSlotSelect = (slot: Slot) => {
    setSelectedSlot(slot);
    setIsConfirming(true);
  };

  const handleConfirm = async () => {
    if (!selectedSlot) return;

    try {
      await bookSlot.mutateAsync({
        slotStart: selectedSlot.start,
        interviewerId: selectedSlot.interviewerId,
      });
      navigate(`/confirmation/${token}`);
    } catch (err) {
      // Error is handled by the mutation
    }
  };

  const handleBack = () => {
    setSelectedSlot(null);
    setIsConfirming(false);
  };

  return (
    <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Vorstellungsgespräch buchen
          </h1>
          <p className="text-gray-600">
            Hallo {data.invitation.bewerberName}, bitte wählen Sie einen passenden Termin.
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {!isConfirming ? (
            <>
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Verfügbare Termine
              </h2>
              {data.slots.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  Derzeit sind keine Termine verfügbar. Bitte versuchen Sie es später erneut.
                </p>
              ) : (
                <SlotPicker slots={data.slots} onSelect={handleSlotSelect} />
              )}
            </>
          ) : (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900">
                Termin bestätigen
              </h2>

              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p className="text-sm text-gray-500">Ausgewählter Termin:</p>
                <p className="text-lg font-medium text-gray-900">
                  {new Date(selectedSlot!.start).toLocaleDateString('de-DE', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
                <p className="text-lg text-gray-700">
                  {new Date(selectedSlot!.start).toLocaleTimeString('de-DE', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}{' '}
                  -{' '}
                  {new Date(selectedSlot!.end).toLocaleTimeString('de-DE', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
                <p className="text-sm text-gray-500">
                  mit {selectedSlot!.interviewerName}
                </p>
              </div>

              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                <p className="text-sm text-primary-800">
                  Das Gespräch findet als Microsoft Teams Meeting statt. Sie erhalten
                  nach der Buchung einen Link zum Meeting.
                </p>
              </div>

              {bookSlot.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">
                    {(bookSlot.error as Error).message ||
                      'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.'}
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleBack}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Zurück
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={bookSlot.isPending}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {bookSlot.isPending ? 'Wird gebucht...' : 'Termin buchen'}
                </button>
              </div>
            </div>
          )}
        </div>
    </div>
  );
}
